import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import classnames from 'classnames';

import Button from '@components/Button';
import Input from '@components/Input';
import Tile from '@components/Tile';

import { clearAllAlerts } from '@actions/appState';
import { loginRequest, loginSuccess } from '@actions/auth';

import { getEmptyErrorState } from '@utils/AppUtil';
import { getUserProfile, isLoggedIn } from '@utils/AuthUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

import './style.scss';

interface ILoginState {
  user: {
    email: string;
    password: string;
  };
  error: {
    email: IFieldValidation;
    password: IFieldValidation;
  };
}

interface ILoginProps extends RouteComponentProps<{}> {
  userProfile: IUserProfile;
  loginSuccess: TLoginSuccess;
  loginRequest: TLoginRequest;
  clearAllAlerts: TClearAllAlerts;
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

class Login extends React.Component<ILoginProps, ILoginState> {
  constructor(props: ILoginProps) {
    super(props);
    this.state = this.getEmptyState();
  }

  componentDidMount() {
    if (isLoggedIn()) {
      const userProfile: IUserProfile = getUserProfile();
      this.redirectUser(userProfile);
    }
  }

  componentWillReceiveProps(nextProps: ILoginProps) {
    if (nextProps.userProfile !== this.props.userProfile) {
      this.redirectUser(nextProps.userProfile);
    }
  }

  getEmptyState = (): ILoginState => ({
    user: {
      email: '',
      password: '',
    },
    error: {
      email: getEmptyErrorState(),
      password: getEmptyErrorState(),
    },
  });

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        [name]: value,
      },
    }));
  };

  redirectUser = (userProfile: IUserProfile) => {
    this.props.loginSuccess(userProfile);

    this.props.history.push('/');
  };

  validateForm = (): boolean => {
    const error = this.getEmptyState().error;
    const { user } = this.state;
    let isValid = true;

    if (cyberdyneValidator.isEmptyString(user.email)) {
      error.email.errorState = IValidationState.ERROR;
      error.email.errorMessage = errMsgGen.getMandatoryFieldMessage('Email');

      isValid = false;
    } else if (!cyberdyneValidator.isEmail(user.email.trim())) {
      error.email.errorState = IValidationState.ERROR;
      error.email.errorMessage = 'Enter Valid Email';

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(user.password)) {
      error.password.errorState = IValidationState.ERROR;
      error.password.errorMessage = errMsgGen.getMandatoryFieldMessage('Password');

      isValid = false;
    }

    this.setState({ error });

    return isValid;
  };

  handleSubmit = (e: React.SyntheticEvent<any>) => {
    if (this.validateForm()) {
      const { user } = this.state;

      this.props.clearAllAlerts();
      this.props.loginRequest(user.email.trim(), user.password, null);
    }
  };

  onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      this.handleSubmit(e);
    }
  };

  render() {
    const { user, error } = this.state;
    const title = 'Comcate';

    return (
      <div className={classnames('login')} onKeyDown={this.onKeyDown}>
        <Tile className={classnames('login__form', 'flex-column--center')} title={title}>
          <Input
            field={{
              isRequired: true,
              label: 'Email',
              type: CustomFieldType.TEXT,
              value: user.email.trim(),
            }}
            name="email"
            placeholder="Enter Email"
            error={error.email}
            onChange={this.handleChange}
            width={12}
          />
          <Input
            field={{
              isRequired: true,
              label: 'Password',
              type: 'password',
              value: user.password,
            }}
            error={error.password}
            placeholder="Enter Password"
            name="password"
            onChange={this.handleChange}
            width={12}
          />
          <Button onClick={this.handleSubmit} bsStyle={ButtonStyle.PRIMARY} content="Login" />
        </Tile>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  userProfile: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch: any) => ({
  loginRequest: (email: string, password: string, agencyId: number) =>
    dispatch(loginRequest(email, password, agencyId)),
  loginSuccess: (userProfile: IUserProfile) => dispatch(loginSuccess(userProfile)),
  clearAllAlerts: () => dispatch(clearAllAlerts()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
